import {
  type FC,
  useState,
  useEffect,
  Children,
  forwardRef,
  useRef,
  useImperativeHandle,
  TouchEvent,
  LegacyRef,
} from 'react';

import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import { useDeviceTypeMediaQuery } from '../hooks/useDeviceTypeMediaQuery';
import type {
  DefaultCarouselPropTypes,
  GetCarouselTimeoutLeftPropType,
  IntervalActionPropType,
  TouchEventEndPropType,
} from './types/propTypes';
import CarouselDot from './CarouselDot';
import styles from './Carousel.module.scss';
import { GUTTER_SIZE } from './types/enums';
import {
  checkIsValueDefined,
  checkIsValueZero,
  customRound,
  dotsToDisplayArr,
  getCarouselContainerConfig,
  getCarouselEvent,
  getDotsPadding,
} from './utils';
import FiniteCarousel from './FiniteCarousel';

const cx = classNames.bind(styles);

const handleTouchEnd = ({
  ref,
  touchPosition,
  pageOffset,
  setPageOffset,
  dotsToDisplay,
  setTouchPosition,
}: TouchEventEndPropType) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { touchstart, touchmove }: any = touchPosition;
  const diffX = touchstart - touchmove;
  if (touchmove && diffX) {
    if (diffX > 0) {
      setPageOffset({
        page: Math.min(pageOffset.page + 1, dotsToDisplay),
      });
    } else {
      setPageOffset({
        page: Math.max(pageOffset.page - 1, 0),
      });
    }
  }
  setTouchPosition({ touchstart: 0, touchmove: 0 });
  ref.current?.classList.add(styles['scroll-control'] as string);
};

const intervalAction = ({
  intervalId,
  setIntervalId,
  isAutoScrollEnable,
  setPageOffset,
  dotsToDisplay,
  noOfScrollSecs,
}: IntervalActionPropType) => {
  // Remove AutoScroll Interval
  const removeInterval = () => clearInterval(intervalId);
  // Resume AutoScroll Interval
  const resumeInterval = () => {
    removeInterval();
    const interval = setInterval(() => {
      setPageOffset((val: { page: number }) => {
        if (val.page >= dotsToDisplay - 1) {
          removeInterval();
          return { page: val.page };
        }
        return { page: checkIsValueDefined(val.page) + 1 };
      });
    }, noOfScrollSecs * 1000);
    setIntervalId(isAutoScrollEnable ? interval : undefined);
  };
  return { removeInterval, resumeInterval };
};
const getCarouselTimeoutLeft = ({
  isTileCarousel,
  carouselItem,
  carouselContainer,
  dotsToDisplay,
  page,
  currentSlide,
}: GetCarouselTimeoutLeftPropType) => {
  const currentSlideValue = checkIsValueDefined(currentSlide.slide);
  const containerScrollWidth = checkIsValueDefined(carouselContainer.current?.scrollWidth);
  const leftToScroll =
    dotsToDisplay >= page + 1 ? Math.ceil((containerScrollWidth / dotsToDisplay) * page) : containerScrollWidth;
  return isTileCarousel ? leftToScroll : carouselItem?.current?.[currentSlideValue]?.offsetLeft;
};

const DefaultCarousel: FC<DefaultCarouselPropTypes> = forwardRef<HTMLDivElement | undefined, DefaultCarouselPropTypes>(
  (
    {
      id,
      children,
      hideButtons,
      hideDots,
      isAutoScrollEnable,
      isTileCarousel = true,
      noOfScrollSecs = 5,
      pauseOnMouseOver,
      gutterSize = GUTTER_SIZE.MEDIUM,
      className = '',
      dotsPaddingTop,
    },
    ref,
  ) => {
    const [itemWidth, setItemWidth] = useState({
      width: 0,
    });
    const [touchPosition, setTouchPosition] = useState<Record<string, number>>({
      touchstart: 0, //event names
      touchmove: 0,
    });
    // Ref for Container Div to calculate total width
    const carouselContainer = useRef<HTMLDivElement>();
    useImperativeHandle(ref, () => carouselContainer.current);
    // Ref for Items(slides) Div to calculate width of items
    const carouselItem = useRef<HTMLDivElement[]>([]);
    // State to maintain count of slides we can show in page
    const [noOfSlides, setNoOfSlides] = useState({
      slidesCount: 1,
      originalCount: 0,
    });
    const [currentSlide, setCurrentSlide] = useState<{
      slide: number;
    }>({
      slide: 0,
    });
    // State for Dots page change
    const [pageOffset, setPageOffset] = useState<{
      page: number;
    }>({ page: 0 });
    // State for page change on manual drag or scroll
    const [page, setPage] = useState<null | number>(0);
    // No. of Dots to show
    const [dotsToDisplay, setDotsToDisplay] = useState(1);
    const [intervalId, setIntervalId] = useState<string | number | undefined | NodeJS.Timeout>(undefined);
    const [dotsPaddingTopValue, setDotsPaddingTopValue] = useState<number | string>();

    const { isMobile, isTablet } = useDeviceTypeMediaQuery();

    const { removeInterval, resumeInterval } = intervalAction({
      intervalId,
      setIntervalId,
      setPageOffset,
      dotsToDisplay,
      noOfScrollSecs,
      ...{ ...optionalProp('isAutoScrollEnable', isAutoScrollEnable) },
    });

    useEffect(() => {
      // In case of a gap between dots and the slider item, we maintain the gap based on the user's device.
      // This is applicable only to the title carousel.
      const padding = getDotsPadding(isMobile, isTablet, dotsPaddingTop);
      setDotsPaddingTopValue(padding);
    }, [isMobile, isTablet, dotsPaddingTop]);

    // Handle mouse scroll or drag scroll event
    const scrollHandler = () => {
      carouselContainer?.current?.removeEventListener('scroll', () => 0);
      carouselContainer?.current?.addEventListener('scroll', () => scrollListener());
    };

    // we need to calculate page number based on scroll
    const scrollListener = () => {
      const childArr = Children.count(children);
      const { scrollWidth, scrollLeft } = getCarouselContainerConfig(carouselContainer);
      const containerScrollLeft = checkIsValueDefined(scrollLeft);
      const containerScrollWidth = checkIsValueDefined(scrollWidth);
      if (isTileCarousel) {
        if (!dotsToDisplay || !containerScrollWidth) {
          setPage(0);
          return;
        }
        const viewPerScrollWidth = Math.ceil(containerScrollWidth / dotsToDisplay);
        const currentPage = customRound(containerScrollLeft / viewPerScrollWidth);
        setPage(Math.max(0, Math.min(currentPage, dotsToDisplay - 1)));
      } else {
        const page = Math.ceil(
          // in case of Hero Banner we dont have in between gap of 16px but
          // our first tile starts from 16px padding left thats why adding 16
          ((containerScrollLeft + 16) / containerScrollWidth) * childArr,
        );
        const raw = checkIsValueZero(page) - 1;
        setPage(Math.max(0, Math.min(raw, childArr - 1)));
      }
    };

    // PageOffset is updated by Buttons, Swipe event and Dots
    useEffect(() => {
      setCurrentSlide({ slide: pageOffset.page * noOfSlides.slidesCount });
    }, [pageOffset]);

    // Change of Slide
    useEffect(() => {
      /**
       * @description setTimeout let UI reflect items on initial load
       * After DOM render and ref initialization, scrollTo function available to use.
       */
      setTimeout(() => {
        carouselContainer?.current?.scrollTo({
          behavior: 'smooth', // TO enable smooth scroll
          ...{
            ...optionalProp(
              'left',
              getCarouselTimeoutLeft({
                isTileCarousel,
                carouselItem,
                carouselContainer,
                dotsToDisplay,
                page: pageOffset.page,
                currentSlide,
              }),
            ),
          },
        });
      }, 0);
      setItemWidth({ width: carouselItem?.current?.[0]?.clientWidth as number });
      scrollHandler();
    }, [currentSlide, children]);

    // Based on width of an item we calculate no of slides/items we can fit in a single frame.
    useEffect(() => {
      const { clientWidth } = getCarouselContainerConfig(carouselContainer);
      if (clientWidth && itemWidth.width) {
        let possibleItems = Math.floor(clientWidth / itemWidth.width);
        // 16 is gap between two titles + 16px padding left on first tile.
        possibleItems = checkIsValueZero(Math.floor((clientWidth - possibleItems * 16) / itemWidth.width));
        setNoOfSlides({ slidesCount: possibleItems, originalCount: possibleItems });
      }
    }, [itemWidth]);
    useEffect(() => {
      const { clientWidth, scrollWidth } = getCarouselContainerConfig(carouselContainer);
      let dotCount = Children.count(children) / (isTileCarousel ? noOfSlides.slidesCount : 1);
      if (clientWidth < itemWidth.width) {
        dotCount = scrollWidth / clientWidth;
      }
      setDotsToDisplay(Math.ceil(dotCount));
    }, [noOfSlides]);

    useEffect(() => {
      const timer = setTimeout(() => {
        setItemWidth({ width: carouselItem?.current?.[0]?.clientWidth as number });
      }, 0);
      // If AutoScroll is enabled set interval
      if (isAutoScrollEnable) {
        resumeInterval();
      } else {
        removeInterval();
      }
      return () => {
        removeInterval();
        clearTimeout(timer);
      };
    }, [isAutoScrollEnable, dotsToDisplay, noOfScrollSecs, noOfSlides.slidesCount]);

    const handleTouch = (e: TouchEvent<HTMLDivElement>) => {
      carouselContainer.current?.classList.remove(styles['scroll-control'] as string);
      setTouchPosition((prevState) => ({
        ...prevState,
        [e.type]: e.touches[0]?.clientX as number,
      }));
    };
    const CCarouselClassname = cx('carousel', {
      'tile-carousel': isTileCarousel,
      'banner-carousel': !isTileCarousel,
      'hide-dots': hideDots,
    });
    return (
      <div className={`${styles['container-carousel']} ${className}`}>
        <div
          id={id}
          data-testid="carousel"
          ref={carouselContainer as LegacyRef<HTMLDivElement>}
          className={CCarouselClassname}
          {...getCarouselEvent({
            removeInterval,
            resumeInterval,
            ...{ ...optionalProp('isAutoScrollEnable', isAutoScrollEnable) },
            ...{ ...optionalProp('pauseOnMouseOver', pauseOnMouseOver) },
          })}
          onTouchStart={handleTouch}
          onTouchMove={handleTouch}
          onTouchEnd={() =>
            handleTouchEnd({
              ref: carouselContainer,
              touchPosition,
              pageOffset,
              setPageOffset,
              dotsToDisplay,
              setTouchPosition,
            })
          }
        >
          <FiniteCarousel
            page={page}
            currentSlide={currentSlide}
            carouselItem={carouselItem}
            carouselContainer={carouselContainer}
            setCurrentSlide={setCurrentSlide}
            setPageOffset={setPageOffset}
            dotsToDisplay={dotsToDisplay}
            gutterSize={gutterSize}
            isTileCarousel={isTileCarousel}
            pageOffset={pageOffset}
            {...(isAutoScrollEnable && { resumeInterval })}
            {...optionalProp('hideButtons', hideButtons)}
          >
            {children}
          </FiniteCarousel>
        </div>
        {!hideDots && dotsToDisplay > 1 && (
          <div
            className={`${styles['carousel-ellipses']} ${!isTileCarousel ? styles['inset'] : ''}`}
            style={{ paddingTop: isTileCarousel ? dotsPaddingTopValue : 0 }}
            data-testid="carousel-dot-container"
          >
            {[...dotsToDisplayArr(dotsToDisplay)].map((_, i) => (
              <CarouselDot
                index={i}
                key={`dot${+i}`}
                isActive={page === i}
                clickHandler={() => {
                  if (isAutoScrollEnable) {
                    resumeInterval();
                  }
                  return setPageOffset({
                    page: i,
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);
DefaultCarousel.displayName = 'DefaultCarousel';
export default DefaultCarousel;
